import React, { useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, ActivityIndicator } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { formatDateTime } from '../../src/utils/timezone';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import Card from '../../src/components/Card';
import ContentContainer from '../../src/components/ContentContainer';
import ReportProblemModal from '../../src/components/ReportProblemModal';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { rs } from '../../src/utils/responsive';
import PageFooter from '../../src/components/PageFooter';

const RATABLE_TYPES = ['automatic', 'self-serve', 'bundle-wash', 'subscription-wash'];
function toRatingWashType(t: string) { return t === 'bundle-wash' ? 'bundle' : t === 'subscription-wash' ? 'subscription' : t; }

interface Transaction {
  readonly id: string; readonly type: string; readonly tierName: string | null;
  readonly durationMinutes: number | null; readonly amount: number; readonly status: string;
  readonly createdAt: string; readonly locationName: string | null; readonly bayName: string | null;
  readonly rating: number | null;
  readonly reported?: boolean;
}

function getTxLabel(tx: Transaction) {
  if (tx.type === 'automatic') return tx.tierName || 'Wash';
  if (tx.type === 'vacuum') return 'Vacuum';
  if (tx.type === 'vending') return 'Vending';
  if (tx.type === 'bundle-wash') return 'Bundle Wash';
  if (tx.type === 'subscription-wash') return 'Member Wash';
  if (tx.type === 'bundle-purchase') return `Bundle: ${tx.tierName || 'Purchase'}`;
  const totalSecs = Math.round((tx.durationMinutes || 0) * 60);
  return `${Math.floor(totalSecs / 60)}:${(totalSecs % 60).toString().padStart(2, '0')} wash`;
}

function getTxIcon(tx: Transaction, colors: any) {
  if (tx.type === 'automatic') return { name: 'car', color: colors.primary, bg: colors.primary + '15' };
  if (tx.type === 'vacuum') return { name: 'sparkles', color: colors.primary, bg: colors.primary + '15' };
  if (tx.type === 'vending') return { name: 'fast-food', color: colors.primary, bg: colors.primary + '15' };
  return { name: 'timer', color: colors.primary, bg: colors.primary + '15' };
}

export default function UserHistoryScreen() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [reportTx, setReportTx] = useState<Transaction | null>(null);
  const [ratingItemId, setRatingItemId] = useState<string | null>(null);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const submitRating = async (item: Transaction, stars: number) => {
    setRatingSubmitting(true);
    try {
      await api('/api/ratings', { method: 'POST', body: { washType: toRatingWashType(item.type), washId: item.id, rating: stars } });
      setTransactions(prev => prev.map(t => t.id === item.id ? { ...t, rating: stars } : t));
      setRatingItemId(null);
    } catch { toast.show({ type: 'error', title: 'Error', message: 'Could not submit rating' }); }
    finally { setRatingSubmitting(false); }
  };

  const fetchTx = async (p = page, l = limit, s = search) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(l) });
      if (s.trim()) params.set('search', s.trim());
      const data = await api<{ transactions: Transaction[]; total: number; page: number; limit: number }>(
        `/api/transactions?${params}`
      );
      setTransactions(data.transactions);
      setTotal(data.total);
      setPage(data.page);
      setLimit(data.limit);
    } catch (err) { console.error('fetch transactions:', err); }
    finally { setLoading(false); }
  };

  const handleSearch = (text: string) => {
    setSearch(text);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); fetchTx(1, limit, text); }, 400);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const allData = await api<Transaction[]>('/api/transactions');
      const rows = allData.map(tx => ({
        Date: formatDateTime(tx.createdAt), Type: getTxLabel(tx), Status: tx.status,
        Amount: (tx.amount / 100).toFixed(2), Location: tx.locationName || '', Bay: tx.bayName || '',
        Rating: tx.rating != null ? String(tx.rating) : '',
      }));
      const headers = Object.keys(rows[0] || {});
      const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r as any)[h] || ''}"`).join(','))].join('\n');
      const { exportCsvFromString } = await import('../../src/utils/fileExport');
      await exportCsvFromString(csv, 'wash-history.csv');
    } catch (err) { console.error('CSV export failed:', err); }
    finally { setExporting(false); }
  };

  useFocusEffect(useCallback(() => { fetchTx(1, 10, ''); }, []));

  return (
    <View style={styles.container}>
      <ContentContainer maxWidth={720} footer={false}>
        <ScrollView contentContainerStyle={styles.content}>
          <Card style={styles.section}>
            <Text style={styles.sectionTitle}>Wash History</Text>

            {/* Search + Export */}
            <View style={styles.toolbar}>
              <View style={styles.searchWrap}>
                <Ionicons name="search" size={16} color={colors.textLight} />
                <TextInput style={styles.searchInput} placeholder="Search..." placeholderTextColor={colors.textLight}
                  value={search} onChangeText={handleSearch} returnKeyType="search" maxLength={100} />
                {search ? <TouchableOpacity onPress={() => { setSearch(''); fetchTx(1, limit, ''); }} hitSlop={8}><Ionicons name="close-circle" size={16} color={colors.textLight} /></TouchableOpacity> : null}
              </View>
              <TouchableOpacity onPress={handleExport} style={styles.csvBtn} disabled={exporting}>
                <Ionicons name="download-outline" size={16} color={colors.white} />
                <Text style={styles.csvText}>{exporting ? '...' : 'CSV'}</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.txCount}>{total} wash{total !== 1 ? 'es' : ''}</Text>

            {transactions.map(item => {
              const icon = getTxIcon(item, colors);
              return (
                <View key={item.id} style={styles.txRow}>
                  <View style={[styles.txIcon, { backgroundColor: icon.bg }]}>
                    <Ionicons name={icon.name as any} size={16} color={icon.color} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.txTitle}>
                      {getTxLabel(item)}{item.bayName ? ` · ${item.bayName}` : ''}
                    </Text>
                    <Text style={styles.txDetail}>
                      {item.locationName || ''} · {formatDateTime(item.createdAt)}
                    </Text>
                    {ratingItemId === item.id ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 2 }}>
                        {ratingSubmitting ? <ActivityIndicator size="small" color={colors.primary} /> :
                          [1,2,3,4,5].map(s => <TouchableOpacity key={s} onPress={() => submitRating(item, s)} hitSlop={4}><Ionicons name={s <= (item.rating ?? 0) ? 'star' : 'star-outline'} size={20} color={colors.warning} /></TouchableOpacity>)}
                      </View>
                    ) : item.rating != null ? (
                      <TouchableOpacity onPress={() => RATABLE_TYPES.includes(item.type) && setRatingItemId(item.id)} disabled={!RATABLE_TYPES.includes(item.type)} style={{ flexDirection: 'row', marginTop: 2 }}>
                        {[1,2,3,4,5].map(s => <Ionicons key={s} name={s <= item.rating! ? 'star' : 'star-outline'} size={11} color={colors.warning} />)}
                      </TouchableOpacity>
                    ) : RATABLE_TYPES.includes(item.type) ? (
                      <TouchableOpacity onPress={() => setRatingItemId(item.id)} style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
                        <Ionicons name="star-outline" size={11} color={colors.primary} />
                        <Text style={{ fontSize: fontSize.xs, color: colors.primary, marginLeft: 2 }}>Rate</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>
                  <View style={styles.txRight}>
                    <Text style={styles.txAmount}>${(item.amount / 100).toFixed(2)}</Text>
                    {item.reported ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2, gap: 3 }}>
                        <Ionicons name="flag" size={14} color={colors.danger} />
                        <Text style={{ fontSize: fontSize.xs, color: colors.danger, fontWeight: fontWeight.semibold as any }}>Reported</Text>
                      </View>
                    ) : (
                      <TouchableOpacity onPress={() => setReportTx(item)} hitSlop={8}>
                        <Ionicons name="flag-outline" size={14} color={colors.danger} />
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              );
            })}

            {/* Always 10/page pagination — search hits the backend so the
                returned total + page reflect the filter. */}
            {total > 10 && (() => {
              const totalPages = Math.max(1, Math.ceil(total / 10));
              return (
                <View style={styles.pageNav}>
                  <TouchableOpacity onPress={() => fetchTx(page - 1, 10, search)} disabled={page <= 1} style={[styles.pageBtn, page <= 1 && { opacity: 0.4 }]}>
                    <Ionicons name="chevron-back" size={16} color={colors.primary} />
                    <Text style={styles.pageBtnText}>Prev</Text>
                  </TouchableOpacity>
                  <Text style={styles.pageText}>Page {page} of {totalPages}</Text>
                  <TouchableOpacity onPress={() => fetchTx(page + 1, 10, search)} disabled={page >= totalPages} style={[styles.pageBtn, page >= totalPages && { opacity: 0.4 }]}>
                    <Text style={styles.pageBtnText}>Next</Text>
                    <Ionicons name="chevron-forward" size={16} color={colors.primary} />
                  </TouchableOpacity>
                </View>
              );
            })()}
            {total === 0 && !loading && (
              <View style={styles.empty}>
                <Ionicons name="water-outline" size={48} color={colors.textLight} />
                <Text style={styles.emptyText}>No washes yet</Text>
              </View>
            )}
          </Card>
          <PageFooter />
        </ScrollView>
      </ContentContainer>
      {reportTx && (
        <ReportProblemModal visible={!!reportTx} onClose={() => setReportTx(null)}
          transactionId={reportTx.id} locationName={reportTx.locationName || undefined}
          bayName={reportTx.bayName || undefined} amount={reportTx.amount}
          date={formatDateTime(reportTx.createdAt)} type={reportTx.type}
          tierName={reportTx.tierName || undefined} durationMinutes={reportTx.durationMinutes || undefined}
          onSuccess={() => setTransactions(prev => prev.map(t => t.id === reportTx.id ? { ...t, reported: true } : t))} />
      )}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  section: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  toolbar: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm },
  searchWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6 },
  searchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, padding: 0 },
  csvBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6, gap: 4 },
  csvText: { color: c.white, fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  txCount: { fontSize: fontSize.xs, color: c.textLight, marginBottom: spacing.sm },
  txRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  txIcon: { width: rs(30), height: rs(30), borderRadius: rs(15), justifyContent: 'center', alignItems: 'center', marginRight: spacing.sm },
  txTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  txDetail: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  txRight: { alignItems: 'flex-end', gap: 4 },
  txAmount: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  showMoreBtn: { alignItems: 'center', paddingVertical: spacing.md },
  showMoreText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  pageNav: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm },
  pageBtn: { flexDirection: 'row', alignItems: 'center', gap: 2, paddingVertical: spacing.xs, paddingHorizontal: spacing.sm },
  pageBtnText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  pageText: { fontSize: fontSize.xs, color: c.textSecondary },
  empty: { alignItems: 'center', paddingVertical: spacing.xxl },
  emptyText: { fontSize: fontSize.md, color: c.textLight, marginTop: spacing.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
